import { supabase } from './supabase';
import { v4 as uuidv4 } from 'uuid';

// Define storage buckets
export const STORAGE_BUCKETS = {
  PROFILE_IMAGES: 'profile-images',
  SNAP_IMAGES: 'snap-images',
  SNAP_CARDS: 'snap-cards',
};

// Upload a profile image
export const uploadProfileImage = async (file: File): Promise<{ url: string | null; error: Error | null }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Generate a unique file name with user ID as folder prefix
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${uuidv4()}.${fileExt}`;

    // Upload the file
    const { error: uploadError, data } = await supabase.storage
      .from(STORAGE_BUCKETS.PROFILE_IMAGES)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) {
      throw uploadError;
    }

    // Get the public URL
    const { data: urlData } = supabase.storage
      .from(STORAGE_BUCKETS.PROFILE_IMAGES)
      .getPublicUrl(fileName);

    return { url: urlData.publicUrl, error: null };
  } catch (error) {
    console.error('Error uploading profile image:', error);
    return { url: null, error: error as Error };
  }
};

// Upload a SNAP image
export const uploadSnapImage = async (file: File): Promise<{ url: string | null; error: Error | null }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Generate a unique file name with user ID as folder prefix
    const fileExt = file.name.split('.').pop();
    const uniqueId = uuidv4();
    const fileName = `${user.id}/${uniqueId}.${fileExt}`;

    console.log(`Uploading snap image: ${fileName}`);

    // Upload the file
    const { error: uploadError, data } = await supabase.storage
      .from(STORAGE_BUCKETS.SNAP_IMAGES)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true,
      });

    if (uploadError) {
      console.error('Error uploading snap image:', uploadError);
      throw uploadError;
    }

    // Get the public URL
    const { data: urlData } = supabase.storage
      .from(STORAGE_BUCKETS.SNAP_IMAGES)
      .getPublicUrl(fileName);

    console.log(`Uploaded snap image, public URL: ${urlData.publicUrl}`);
    return { url: urlData.publicUrl, error: null };
  } catch (error) {
    console.error('Error uploading SNAP image:', error);
    return { url: null, error: error as Error };
  }
};

// Upload a SNAP card image
export const uploadSnapCardImage = async (snapId: string, file: File): Promise<{ url: string | null; error: Error | null }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Use the snapId directly as the filename for better URL structure
    // This ensures the image is accessible at /snap-cards/{snapId}.jpg
    const fileName = `${snapId}.jpg`;

    // Upload the file
    const { error: uploadError, data } = await supabase.storage
      .from(STORAGE_BUCKETS.SNAP_CARDS)
      .upload(fileName, file, {
        contentType: 'image/jpeg',
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      throw uploadError;
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(STORAGE_BUCKETS.SNAP_CARDS)
      .getPublicUrl(fileName);

    return { url: publicUrl, error: null };
  } catch (error) {
    console.error('Error uploading SNAP card image:', error);
    return { url: null, error: error as Error };
  }
};

// Delete a profile image
export const deleteProfileImage = async (url: string): Promise<{ success: boolean; error: Error | null }> => {
  try {
    // Check if URL is empty or null
    if (!url) {
      return { success: true, error: null };
    }
    
    // Handle default avatar case - no need to delete
    if (url.includes('upload.wikimedia.org') || url.includes('unsplash.com')) {
      return { success: true, error: null };
    }

    try {
      // Extract the file path from the URL
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      
      // Find the bucket name in the path
      const bucketIndex = pathParts.findIndex(part => part === STORAGE_BUCKETS.PROFILE_IMAGES);
      if (bucketIndex === -1 || bucketIndex === 0) {
        return { success: false, error: new Error('Invalid URL format') };
      }
      
      // Get the path after the bucket name
      const filePath = pathParts.slice(bucketIndex + 1).join('/');

      const { error, data } = await supabase.storage
        .from(STORAGE_BUCKETS.PROFILE_IMAGES)
        .remove([filePath]);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (parseError) {
      return { success: false, error: parseError as Error };
    }
  } catch (error) {
    console.error('Error deleting profile image:', error);
    return { success: false, error: error as Error };
  }
};

// Delete a SNAP image
export const deleteSnapImage = async (url: string): Promise<{ success: boolean; error: Error | null }> => {
  try {
    // Check if URL is empty or null
    if (!url) {
      return { success: true, error: null };
    }
    
    // Handle default image case - no need to delete
    if (url.includes('unsplash.com')) {
      return { success: true, error: null };
    }

    try {
      // Extract the file path from the URL
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      
      // Find the bucket name in the path
      const bucketIndex = pathParts.findIndex(part => part === STORAGE_BUCKETS.SNAP_IMAGES);
      if (bucketIndex === -1) {
        return { success: false, error: new Error('Invalid URL format') };
      }
      
      // Get the path after the bucket name
      const filePath = pathParts.slice(bucketIndex + 1).join('/');

      const { error, data } = await supabase.storage
        .from(STORAGE_BUCKETS.SNAP_IMAGES)
        .remove([filePath]);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (parseError) {
      return { success: false, error: parseError as Error };
    }
  } catch (error) {
    console.error('Error deleting SNAP image:', error);
    return { success: false, error: error as Error };
  }
};

// Delete a SNAP card image
export const deleteSnapCardImage = async (url: string): Promise<{ success: boolean; error: Error | null }> => {
  try {
    // Check if URL is empty or null
    if (!url) {
      return { success: true, error: null };
    }
    
    // Handle placeholder image case - no need to delete
    if (url.includes('via.placeholder.com') || url.includes('unsplash.com')) {
      return { success: true, error: null };
    }

    try {
      // Extract the file path from the URL
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/');
      
      // Find the bucket name in the path
      const bucketIndex = pathParts.findIndex(part => part === STORAGE_BUCKETS.SNAP_CARDS);
      if (bucketIndex === -1) {
        return { success: false, error: new Error('Invalid URL format') };
      }
      
      // Get the path after the bucket name
      const filePath = pathParts.slice(bucketIndex + 1).join('/');

      const { error, data } = await supabase.storage
        .from(STORAGE_BUCKETS.SNAP_CARDS)
        .remove([filePath]);

      if (error) {
        throw error;
      }

      return { success: true, error: null };
    } catch (parseError) {
      return { success: false, error: parseError as Error };
    }
  } catch (error) {
    console.error('Error deleting SNAP card image:', error);
    return { success: false, error: error as Error };
  }
};

// Check if a file exists in storage
export const checkFileExists = async (bucket: string, path: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, 60); // 60 seconds expiry
    
    return !!data && !error;
  } catch (error) {
    console.error('Error checking if file exists:', error);
    return false;
  }
};

// Initialize storage - this is a no-op now since we're not creating buckets from the client
export const initStorage = async () => {
  return { success: true };
};